/**
 * 20261030 PC 핸즈업 브라우저 획득(gUM·getDisplayMedia) 무효 결과 처리 (web-relay R-238~R-240·R-261·R-262, milestone_42 §3.37·§3.39)
 *
 * 왜 있나: 종료 뒤 늦게 돌아온 화면/카메라/마이크 획득이 전역·UI·152·fallback 을 다시 만들었다.
 *          요청마다 ticket 을 열고, 결과를 받은 즉시(await 없이) 원래 호출의 intent 로 판정한다.
 *
 * 계약
 * - beginAcquire(kind): 종료 중/종료 뒤면 null(요청을 시작하지 않는다).
 * - dropIfInvalid(ticket, stream): 무효면 받은 stream 의 트랙 전체를 정지하고 ticket 을 해제한 뒤 true.
 *                                   유효면 false 이고 ticket 은 열린 채 둔다(호출부가 인계 뒤 endAcquire).
 * - endAcquire(ticket): 정확히 1회 해제(중복 무시).
 * - isAcquireIntentOk(intent): 해제 뒤에도 원래 호출이 유효한지 현재 상태로 판정. intent 없으면 true.
 * ticket 해제 전 정지 실패는 종료 스냅샷 실패로 모인다(noteFailure 주입). 해제 뒤 실패는 로그만 남는다.
 */

let api = {
    begin  : () => null,
    end    : () => {},
    isValid: () => true,
    noteFailure: () => {}
};

const log = (msg) => console.log(`HandsUpAcquireGuard - ${msg}`);

/** HandsUpClient 가 한 번 연결한다. */
export const bindAcquireGuard = (deps) => {
    api = { ...api, ...deps };
};

export const beginAcquire = (kind) => {
    try {
        return api.begin(kind);
    } catch (err) {
        log(`ticket 시작 오류 ${err && err.message}`);
        return null;
    }
};

export const endAcquire = (ticket) => {
    if (!ticket) return;
    try {
        api.end(ticket);
    } catch (err) {
        log(`ticket 해제 오류 ${err && err.message}`);
    }
};

export const isAcquireIntentOk = (intent) => {
    if (intent === undefined || intent === null) return true;
    try {
        return api.isValid(intent) === true;
    } catch (err) {
        return false;
    }
};

/** 받은 stream 의 트랙 전체를 멱등 정지한다. 하나가 실패해도 나머지는 계속한다. @returns {Number} 실패 수 */
export const stopAcquiredStream = (stream) => {
    let failures = 0;
    if (!stream || typeof stream.getTracks !== "function") return failures;
    let tracks = [];
    try {
        tracks = stream.getTracks();
    } catch (err) {
        return 1;
    }
    tracks.forEach((track) => {
        try {
            if (track && track.readyState !== "ended") track.stop();
        } catch (err) {
            failures++;
        }
    });
    return failures;
};

/**
 * 요청 반환/실패 직후 동기로 부른다. 무효면 정지·해제 후 true.
 * @param {Object}      ticket
 * @param {MediaStream} [stream] - 실패 갈래면 생략
 * @returns {Boolean} 무효라 버렸으면 true
 */
export const dropIfInvalid = (ticket, stream) => {
    if (!ticket) return false; // ticket 없는 호출(기존 동작)은 판정하지 않는다
    if (isAcquireIntentOk(ticket.intent)) return false;
    try {
        const failures = stopAcquiredStream(stream);
        if (failures > 0) {
            try { api.noteFailure("acquire-stop"); } catch (err) { /* 기록 실패 무시 */ }
        }
        log(`무효 획득(${ticket.kind})을 버렸다. 정지 실패 ${failures}`);
    } finally {
        endAcquire(ticket);
    }
    return true;
};

/** ticket 해제 뒤(intent 만 남은 구간)에 버릴 때 쓴다. 정지 실패는 로그만 남긴다. */
export const dropAfterHandover = (intent, stream, name) => {
    if (isAcquireIntentOk(intent)) return false;
    const failures = stopAcquiredStream(stream);
    log(`인계 뒤 무효(${name}). 받은 stream 정지, 실패 ${failures}`);
    return true;
};

/**
 * legacy navigator.getUserMedia 호출이 동기로 throw 하면 콜백이 오지 않으므로 ticket 을 여기서 해제한다.
 * 원래 오류는 그대로 다시 던진다.
 */
export const requestLegacy = (ticket, fn) => {
    try {
        return fn();
    } catch (err) {
        endAcquire(ticket);
        throw err;
    }
};
